import { useI18n } from '@/i18n'
import { cn } from '@/lib/utils'

/**
 * The waiting state of a surface that fetches its whole content.
 *
 * Why it is a component and not a `<p>`: the product renders on the client, so
 * a report, a facts list or a verification pack starts as one line of text and
 * becomes a document several viewports tall. That swap used to move the footer
 * from inside the viewport to far below it — a layout shift the reader sees as
 * the page jumping under the cursor, and Lighthouse counts it in full.
 *
 * The reservation is a floor, not a guess. Every content surface in this
 * product is taller than one viewport once it has loaded (measured at
 * 1350 × 940: report 6699 px, facts 4540, admin 2601, verification 2570,
 * workspace 2190, ingestion 1236, run 1066 — the shortest still exceeds the
 * 940 px viewport). Reserving `100dvh − 10rem` puts the footer just below the
 * fold *before* the content arrives, and the content then only ever pushes it
 * further down. The arithmetic holds at any viewport height, because <main>
 * adds 6 rem of its own padding and the header a further ~6 rem: the reserved
 * block plus that chrome always exceeds 100dvh, and it scales with the viewport
 * instead of being a pixel guess.
 *
 * The loading line itself stays exactly what it was — first thing in the box,
 * at the top of the page, in the reader's language. Someone on a slow
 * connection still learns that something is happening; they simply no longer
 * get thrown down the page when it finishes.
 */
export function LoadingBlock({ className }: { className?: string }) {
  const { t } = useI18n()

  return (
    <div className={cn('min-h-[calc(100dvh-10rem)]', className)} data-testid="loading-block">
      <p className="text-[13px] text-muted">{t('common.loading')}</p>
    </div>
  )
}
